class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.overlay = this.querySelector('.cart-overlay');
    this.drawer = this.querySelector('.cart-drawer');
    this.subscriptionSection = this.querySelector('.subscription-purchases');
    this.oneTimeSection = this.querySelector('.one-time-purchases');
    this.cartCounter = this.querySelector('.cart-counter');
    this.lastCartItems = []; // Track state between updates
    this.isAddingToCart = false; // Prevent multiple simultaneous requests

    // Single serving sample products with correct variant IDs
    this.singleServingSamples = {
      'chocolate-peanut-butter': {
        id: 43121080959164,
        name: "Organic Plant Protein, Chocolate Peanut Butter, Single Serving - FREE"
      },
      'chocolate': {
        id: 43768233296060,
        name: "KOS Organic Plant Protein, Chocolate, Single Serving - FREE"
      },
      'vanilla': {
        id: 44482041413820,
        name: "Organic Plant Protein, Vanilla, Single Serving - FREE"
      }
    };

    // Eligible 28-serving subscription product IDs (not variant IDs)
    this.eligible28ServingProductIds = [
      8491146805436, // Strawberries & Cream
      459985911847,  // Chocolate
      4658839846978, // Vanilla
      7654308577468, // Chocolate Peanut Butter
      7654306414780, // Salted Caramel Coffee
      7654304055484  // Unflavored & Unsweetened
    ];

    this.toggleCart = this.toggleCart.bind(this);
    this.closeCart = this.closeCart.bind(this);
    this.handleAddToCart = this.handleAddToCart.bind(this);
    this.handleDirectAddToCart = this.handleDirectAddToCart.bind(this);
    this.updateCartDrawer = this.updateCartDrawer.bind(this);
    this.handleQuantityChange = this.handleQuantityChange.bind(this);
    this.updateCartCounter = this.updateCartCounter.bind(this);
  }

  connectedCallback() {
    this.setupEventListeners();
    this.setupDirectAddToCartButtons();
    
    // Initialize cart without managing gifts
    this.initializeCartWithoutGifts();
    
    // Initialize the cart counter
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        this.updateCartCounter(cart.item_count);
      })
      .catch(error => console.error('Error fetching cart:', error));
  }

  async initializeCartWithoutGifts() {
    try {
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      // Update cart counter with total items
      this.updateCartCounter(cart.item_count);
      
      // Just render without managing gifts
      this.renderCartContents(cart.items, false);
      this.updateCartTotal(cart.total_price);
      this.updateCartSummary(cart.item_count);
      this.lastCartItems = cart.items;

      this.drawer.classList.remove('updating');
      this.attachQuantityChangeListeners();
      this.attachRemoveButtonListeners();
    } catch (error) {
      console.error('Error initializing cart:', error);
    }
  }

  updateCartCounter(itemCount) {
    // Handle undefined or null itemCount
    if (itemCount === undefined || itemCount === null) {
      itemCount = 0;
    }
    
    // Ensure itemCount is a number
    const count = Number(itemCount);
    
    // Store old value to check if it changed
    const oldValue = this.cartCounter ? this.cartCounter.textContent.replace(/[()]/g, '') : '0';
    const newValue = count.toString();
    
    // Update cart drawer counter
    if (this.cartCounter) {
      this.cartCounter.textContent = `(${newValue})`;
      
      // Only animate if the value changed
      if (oldValue !== newValue) {
        // Add animation class
        this.cartCounter.classList.add('updated');
        
        // Remove animation class after animation completes
        setTimeout(() => {
          this.cartCounter.classList.remove('updated');
        }, 300);
      }
    }
    
    // Update header cart counters
    document.querySelectorAll('.header-cart-count').forEach(counter => {
      const headerOldValue = counter.textContent;
      counter.textContent = newValue;
      
      // Hide counter if cart is empty
      if (count === 0) {
        counter.style.display = 'none';
      } else {
        counter.style.display = '';
      }
      
      // Only animate if the value changed
      if (headerOldValue !== newValue) {
        counter.classList.add('updated');
        setTimeout(() => {
          counter.classList.remove('updated');
        }, 300);
      }
    });
  }

  setupEventListeners() {
    // Existing event listeners
    this.overlay.addEventListener('click', this.closeCart);

    document.querySelectorAll('.header__icon--cart').forEach((button) => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggleCart();
      });
    });

    document.querySelectorAll('form[action="/cart/add"]').forEach((form) => {
      form.addEventListener('submit', this.handleAddToCart);
    });

    const closeButton = this.drawer.querySelector('.close-button');
    if (closeButton) {
      closeButton.addEventListener('click', this.closeCart);
    }

    // Add checkout button listener
    const checkoutButton = this.drawer.querySelector('#CartDrawerButton');
    if (checkoutButton) {
      checkoutButton.addEventListener('click', (e) => {
        e.preventDefault();
        window.location.href = '/checkout';
      });
    }

    this.attachRemoveButtonListeners();
  }

  // New method to set up direct add-to-cart buttons
  setupDirectAddToCartButtons() {
    // Use event delegation to handle any add-to-cart button in the document
    document.addEventListener('click', async (e) => {
      const shopButton = e.target.closest('.product-card__add-to-cart:not([disabled])');
      if (shopButton && !shopButton.closest('.cart-drawer')) {
        // Prevent default only if it's a link
        if (shopButton.tagName === 'A') {
          e.preventDefault();
        }
        
        // Prevent multiple clicks
        if (this.isAddingToCart) {
          return;
        }
        
        // Get the product data from the card
        const productCard = shopButton.closest('.product-card');
        if (productCard) {
          const productUrl = productCard.querySelector('.product-card__title-link')?.getAttribute('href');
          
          if (productUrl) {
            // Extract variant ID from product URL or data attribute
            let variantId = shopButton.getAttribute('data-variant-id');
            
            // If no data attribute, try to get it from the URL
            if (!variantId && productUrl.includes('variant=')) {
              const match = productUrl.match(/variant=([0-9]+)/);
              if (match && match[1]) {
                variantId = match[1];
              }
            }
            
            // If still no variant ID, look for a hidden input
            if (!variantId) {
              const hiddenInput = productCard.querySelector('input[name="id"]');
              if (hiddenInput) {
                variantId = hiddenInput.value;
              }
            }
            
            if (variantId) {
              // Add visual indicator
              shopButton.classList.add('spinning');
              shopButton.disabled = true;
              
              // Add product directly to cart
              await this.handleDirectAddToCart(variantId);
              
              // Re-enable button after a short delay
              setTimeout(() => {
                shopButton.disabled = false;
              }, 1000);
            } else {
              // If we can't find the variant ID, fall back to the product page
              window.location.href = productUrl;
            }
          }
        }
      }
    });
  }

  // New method to handle direct add to cart
  async handleDirectAddToCart(variantId, quantity = 1, selectedSamples = null) {
    if (this.isAddingToCart) return;
    
    this.isAddingToCart = true;
    this.toggleLoading(true);
    
    try {
      // Add the product to the cart
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: parseInt(variantId),
          quantity: quantity
        }),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      // Get the added item data
      const addedItem = await response.json();
      
      // Check if this is an eligible subscription product and we have selected samples
      if (selectedSamples && selectedSamples.length === 2 && addedItem.selling_plan_allocation) {
        // Get the product ID from the added item  
        const productId = parseInt(addedItem.product_id);
        console.log('Checking product ID:', productId, 'against eligible IDs:', this.eligible28ServingProductIds); // Debug log
        
        if (this.eligible28ServingProductIds.includes(productId)) {
          // Add the selected samples with a small delay between each
          for (const sampleKey of selectedSamples) {
            const sample = this.singleServingSamples[sampleKey];
            if (sample) {
              await new Promise(resolve => setTimeout(resolve, 300));
              await fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  id: sample.id,
                  quantity: 1,
                  properties: {
                    '_gift': 'true',
                    '_parent_variant': variantId
                  }
                }),
              });
            }
          }
        }
      }
      
      // Fetch the full cart to get accurate item count
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      
      this.updateCartCounter(cart.item_count || 0);
      
      // Open cart drawer and update its contents
      this.openCart();
      await this.updateCartDrawer(true); // Force animation for newly added items
    } catch (error) {
      console.error('Error adding product to cart:', error);
      
      // Show error message to user
      if (error.message.includes('429')) {
        alert('Too many requests. Please wait a moment and try again.');
      } else {
        alert('Error adding item to cart. Please try again.');
      }
      
      // Still try to update cart state
      try {
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();
        this.updateCartCounter(cart.item_count || 0);
      } catch (e) {
        this.updateCartCounter(0);
      }
    } finally {
      // Remove spinning indicator from all buttons
      document.querySelectorAll('.product-card__add-to-cart.spinning').forEach(button => {
        button.classList.remove('spinning');
      });
      this.toggleLoading(false);
      
      // Add a small delay before allowing another add to cart
      setTimeout(() => {
        this.isAddingToCart = false;
      }, 500);
    }
  }

  attachRemoveButtonListeners() {
    const removeButtons = this.drawer.querySelectorAll('.remove-button');
    removeButtons.forEach((button) => {
      button.addEventListener('click', this.handleRemoveItem.bind(this));
    });
  }

  async handleRemoveItem(event) {
    const button = event.currentTarget;
    const rootItem = button.closest('.cart-item');
    const line = rootItem.getAttribute('data-line'); // Get the `line` property

    try {
      await this.removeCartItem(line); // Call the existing removeCartItem method
    } catch (error) {
      console.error('Error removing item:', error);
    }
  }

  attachQuantityChangeListeners() {
    const quantityButtons = this.drawer.querySelectorAll('.quantity-selector button');
    quantityButtons.forEach((button) => {
      button.addEventListener('click', this.handleQuantityChange);
    });
  }

  async handleQuantityChange(event) {
    event.preventDefault();

    const button = event.currentTarget;
    const rootItem = button.closest('.cart-item');
    const line = rootItem.getAttribute('data-line'); // Get the `line` property
    const isIncrement = button.dataset.quantity === '+1';

    const quantityElement = rootItem.querySelector('.quantity-selector span');
    const currentQuantity = parseInt(quantityElement.textContent, 10);
    const newQuantity = isIncrement ? currentQuantity + 1 : currentQuantity - 1;

    // If the quantity is 0, remove the item from the cart
    if (newQuantity < 1) {
      await this.removeCartItem(line);
      return;
    }

    // Disable buttons during the update
    this.toggleQuantityButtons(false);

    // Add spin animation to the clicked button
    button.classList.add('spinning');

    try {
      // Update the cart item using the `line` and `quantity`
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          line: parseInt(line, 10),
          quantity: newQuantity,
        }),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      // Get the updated cart data
      const cartData = await response.json();
      this.updateCartCounter(cartData.item_count || 0);

      // Update the cart drawer to reflect changes
      await this.updateCartDrawer();
    } catch (error) {
      console.error('Error updating quantity:', error);
      // Try to refresh cart state
      try {
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();
        this.updateCartCounter(cart.item_count || 0);
      } catch (e) {
        this.updateCartCounter(0);
      }
    } finally {
      // Re-enable buttons and remove spin animation
      button.classList.remove('spinning');
      this.toggleQuantityButtons(true);
    }
  }

  async removeCartItem(line) {
    try {
      this.toggleLoading(true);

      // Remove the item from the cart
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          line: parseInt(line, 10),
          quantity: 0,
        }),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      // Get the updated cart data
      const cartData = await response.json();
      this.updateCartCounter(cartData.item_count || 0);

      // Update the cart drawer to reflect changes
      await this.updateCartDrawer();
    } catch (error) {
      console.error('Error removing item:', error);
      // Try to refresh cart state
      try {
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();
        this.updateCartCounter(cart.item_count || 0);
      } catch (e) {
        this.updateCartCounter(0);
      }
    } finally {
      this.toggleLoading(false);
    }
  }

  toggleQuantityButtons(enable) {
    const buttons = this.drawer.querySelectorAll('.quantity-selector button');
    buttons.forEach((button) => {
      button.disabled = !enable;
    });
  }

  async handleAddToCart(e) {
    e.preventDefault();

    if (this.isAddingToCart) return;
    
    this.isAddingToCart = true;

    // Use e.currentTarget to reliably reference the form that triggered the event.
    const form = e.currentTarget;
    const atcButton = form.querySelector('button[type="submit"]');

    // Check if form has selected samples - they're hidden inputs, not checkboxes
    const sampleInputs = form.querySelectorAll('input[name="selected_samples[]"]');
    const selectedSamples = Array.from(sampleInputs).map(input => input.value);

    console.log('Selected samples:', selectedSamples); // Debug log

    // Add spinning class to show a spinner on the button
    if (atcButton) {
      atcButton.classList.add('spinning');
      atcButton.disabled = true;
    }
    this.toggleLoading(true);

    try {
      const formData = new FormData(form);
      
      // Get the variant ID to check if we need to add samples
      const variantId = formData.get('id');
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      // Get the added item data
      const addedItem = await response.json();
      
      console.log('Added item:', addedItem); // Debug log
      console.log('Has selling plan:', !!addedItem.selling_plan_allocation); // Debug log
      
      // Check if this is an eligible subscription product and we have selected samples
      if (selectedSamples.length === 2 && addedItem.selling_plan_allocation) {
        // Get the product ID from the added item
        const productId = parseInt(addedItem.product_id);
        console.log('Checking product ID:', productId, 'against eligible IDs:', this.eligible28ServingProductIds); // Debug log
        
        if (this.eligible28ServingProductIds.includes(productId)) {
          console.log('Adding gift samples...'); // Debug log
          
          // Add the selected samples with a small delay between each
          for (const sampleKey of selectedSamples) {
            const sample = this.singleServingSamples[sampleKey];
            if (sample) {
              await new Promise(resolve => setTimeout(resolve, 300));
              
              try {
                const sampleData = {
                  id: sample.id,
                  quantity: 1,
                  properties: {
                    '_gift': 'true',
                    '_parent_variant': variantId
                  }
                };
                
                console.log('Attempting to add sample:', sampleData);
                
                const sampleResponse = await fetch('/cart/add.js', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(sampleData),
                });
                
                if (!sampleResponse.ok) {
                  const errorText = await sampleResponse.text();
                  console.error('Failed to add sample:', sample.name, 'Status:', sampleResponse.status, 'Error:', errorText);
                } else {
                  console.log('Successfully added sample:', sample.name);
                }
              } catch (sampleError) {
                console.error('Error adding sample:', sampleError);
              }
            }
          }
        }
      }
      
      // Fetch the full cart to get accurate item count
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      
      this.updateCartCounter(cart.item_count || 0);

      this.openCart();
      await this.updateCartDrawer(true); // Force animation for newly added items
    } catch (error) {
      console.error('Error adding to cart:', error);
      
      // Show error message to user
      if (error.message.includes('429')) {
        alert('Too many requests. Please wait a moment and try again.');
      } else {
        alert('Error adding item to cart. Please try again.');
      }
      
      // Try to update cart state
      try {
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();
        this.updateCartCounter(cart.item_count || 0);
      } catch (e) {
        this.updateCartCounter(0);
      }
    } finally {
      if (atcButton) {
        atcButton.classList.remove('spinning');
        // Re-enable button after a delay
        setTimeout(() => {
          atcButton.disabled = false;
        }, 1000);
      }
      this.toggleLoading(false);
      
      // Add a small delay before allowing another add to cart
      setTimeout(() => {
        this.isAddingToCart = false;
      }, 500);
    }
  }

  async updateCartDrawer(triggerAnimation = false, recursionDepth = 0) {
    try {
      this.drawer.classList.add('updating'); // Show loading spinner

      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      // Update cart counter with total items
      this.updateCartCounter(cart.item_count);
      
      // Always check for orphaned gifts
      let giftsUpdated = await this.manageGifts(cart);

      // If gifts were updated, re-fetch the cart to reflect changes
      if (giftsUpdated) {
        const updatedCartResponse = await fetch('/cart.js');
        const updatedCart = await updatedCartResponse.json();
        
        // Check if there are still more orphaned gifts (in case we had multiple)
        // Limit recursion depth to prevent infinite loops
        if (recursionDepth < 3) {
          const moreGiftsToRemove = await this.checkForOrphanedGifts(updatedCart);
          if (moreGiftsToRemove) {
            console.log('Found more orphaned gifts, updating again...');
            this.drawer.classList.remove('updating');
            return this.updateCartDrawer(triggerAnimation, recursionDepth + 1);
          }
        }
        
        this.renderCartContents(updatedCart.items, triggerAnimation);
        this.updateCartTotal(updatedCart.total_price);
        this.updateCartSummary(updatedCart.item_count);
        this.updateCartCounter(updatedCart.item_count); // Update counter again if gifts changed
        this.lastCartItems = updatedCart.items;
      } else {
        this.renderCartContents(cart.items, triggerAnimation);
        this.updateCartTotal(cart.total_price);
        this.updateCartSummary(cart.item_count);
        this.lastCartItems = cart.items;
      }

      this.drawer.classList.remove('updating');
      this.attachQuantityChangeListeners();
      this.attachRemoveButtonListeners();
    } catch (error) {
      console.error('Error updating cart drawer:', error);
      this.drawer.classList.remove('updating');
    }
  }

  // Helper method to check if there are still orphaned gifts
  async checkForOrphanedGifts(cart) {
    const sampleIds = Object.values(this.singleServingSamples).map(s => s.id);
    
    for (const item of cart.items) {
      if (sampleIds.includes(item.id) && item.properties?._gift === 'true') {
        const parentVariantId = parseInt(item.properties._parent_variant);
        const parentStillInCart = cart.items.some(cartItem => {
          if (cartItem.id === parentVariantId && cartItem.selling_plan_allocation) {
            const productId = parseInt(cartItem.product_id);
            return this.eligible28ServingProductIds.includes(productId);
          }
          return false;
        });
        
        if (!parentStillInCart) {
          return true; // Found at least one orphaned gift
        }
      }
    }
    
    return false;
  }

  updateCartSummary(itemCount) {
    const cartSummaryText = this.drawer.querySelector('.cart-summary-text');
    if (cartSummaryText) {
      cartSummaryText.textContent = `Cart Summary (${itemCount} product${itemCount !== 1 ? 's' : ''})`;
    }
  }

  updateCartTotal(totalPrice) {
    const totalElement = this.drawer.querySelector('.cart-total--value');
    if (totalElement) {
      totalElement.textContent = `$${(totalPrice / 100).toFixed(2)}`;
    }
  }

  async manageGifts(cart) {
    let giftsUpdated = false;
    const itemsToRemove = [];

    // Add delays between gift operations to avoid rate limiting
    const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

    // Check for orphaned gift samples (parent product removed)
    const sampleIds = Object.values(this.singleServingSamples).map(s => s.id);
    
    // First, check if there are ANY eligible subscription products in the cart
    const hasEligibleSubscription = cart.items.some(item => {
      if (item.selling_plan_allocation) {
        const productId = parseInt(item.product_id);
        return this.eligible28ServingProductIds.includes(productId);
      }
      return false;
    });
    
    console.log('Has eligible subscription in cart:', hasEligibleSubscription);
    
    // If there are NO eligible subscriptions, remove ALL gift samples
    if (!hasEligibleSubscription) {
      console.log('No eligible subscriptions found, removing all gift samples');
      
      for (const item of cart.items) {
        if (sampleIds.includes(item.id) && item.properties?._gift === 'true') {
          const lineItem = cart.items.findIndex((cartItem) => cartItem.key === item.key) + 1;
          itemsToRemove.push({
            line: lineItem,
            title: item.product_title
          });
        }
      }
    } else {
      // If there ARE eligible subscriptions, check for orphaned gifts (old logic)
      for (const item of cart.items) {
        if (sampleIds.includes(item.id) && item.properties?._gift === 'true') {
          const parentVariantId = parseInt(item.properties._parent_variant);
          const parentStillInCart = cart.items.some(cartItem => {
            // Check if the parent variant is still in cart with subscription
            if (cartItem.id === parentVariantId && cartItem.selling_plan_allocation) {
              // Get the product ID to check eligibility
              const productId = parseInt(cartItem.product_id);
              return this.eligible28ServingProductIds.includes(productId);
            }
            return false;
          });
          
          console.log('Checking gift item:', item.product_title, 'Parent variant:', parentVariantId, 'Parent still in cart:', parentStillInCart);
          
          if (!parentStillInCart) {
            // Mark for removal
            const lineItem = cart.items.findIndex((cartItem) => cartItem.key === item.key) + 1;
            itemsToRemove.push({
              line: lineItem,
              title: item.product_title
            });
          }
        }
      }
    }

    // Now remove all orphaned gifts
    if (itemsToRemove.length > 0) {
      console.log(`Found ${itemsToRemove.length} gifts to remove`);
      
      // Sort items by line number in descending order to avoid index shifting
      itemsToRemove.sort((a, b) => b.line - a.line);
      
      for (const itemToRemove of itemsToRemove) {
        console.log('Removing gift:', itemToRemove.title);
        await delay(300);
        
        try {
          const removeResponse = await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              line: itemToRemove.line,
              quantity: 0,
            }),
          });
          
          if (!removeResponse.ok) {
            console.error('Failed to remove gift:', itemToRemove.title);
          } else {
            console.log('Successfully removed gift:', itemToRemove.title);
            giftsUpdated = true;
          }
        } catch (error) {
          console.error('Error removing gift:', error);
        }
      }
    }

    return giftsUpdated;
  }

  renderCartContents(items, triggerAnimation) {
    // Reset the section content with just the headings
    this.subscriptionSection.innerHTML = '<h3>Subscriptions</h3>';
    this.oneTimeSection.innerHTML = '<h3>One Time Purchase</h3>';
    
    // Track if we have items in each category
    let hasSubscriptions = false;
    let hasOneTimeItems = false;
    
    // Process each item
    items.forEach((item, index) => {
      const itemHTML = this.createCartItem(item, index);

      if (triggerAnimation && this.isNewItem(item)) {
        this.animateNewItem(itemHTML);
      }

      if (item.selling_plan_allocation) {
        this.subscriptionSection.appendChild(itemHTML);
        hasSubscriptions = true;
      } else {
        this.oneTimeSection.appendChild(itemHTML);
        hasOneTimeItems = true;
      }
    });
    
    // Show/hide sections based on whether they have items
    this.subscriptionSection.style.display = hasSubscriptions ? 'block' : 'none';
    this.oneTimeSection.style.display = hasOneTimeItems ? 'block' : 'none';
  }

  createCartItem(item, index) {
    const div = document.createElement('div');
    div.classList.add('cart-item');
    div.setAttribute('data-line', index + 1);
  
    const sellingPlanName = item.selling_plan_allocation?.selling_plan?.name || '';
    const isGift = item.properties?._gift === 'true';
    const sampleIds = Object.values(this.singleServingSamples).map(s => s.id);
    const isSample = sampleIds.includes(item.id);
  
    if (isGift || isSample) {
      div.innerHTML = `
        <div class="cart-item__image cart-item__image--gift">
          <img src="${item.image}" alt="${item.product_title}">
        </div>
        <div class="cart-item__details">
          <h4 class="cart-item--heading">${item.quantity}x ${item.product_title}</h4>
          ${isGift ? '<span class="gift-label">FREE GIFT</span>' : ''}
        </div>
      `;
    } else {
      const compareAtPrice = item.selling_plan_allocation?.compare_at_price || item.original_price;
      const discountedPrice = item.final_price;
      const hasStrikethroughPrice = compareAtPrice && compareAtPrice > discountedPrice;
      const variant = item.variant_title ? `<div class="cart-item__variant">${item.variant_title}</div>` : '';
      
      // New grid-based layout
      div.innerHTML = `
        <div class="cart-item__image">
          <img src="${item.image}" alt="${item.product_title}">
        </div>
        <h4 class="cart-item--heading">${item.product_title}</h4>
        ${variant}
        ${sellingPlanName ? `<div class="item-selling-plan">${sellingPlanName}</div>` : ''}
        <div class="cart-item--price">
          <p class="cart-item--discounted-price">$${(discountedPrice / 100).toFixed(2)}</p>
          ${
            hasStrikethroughPrice
              ? `<p class="cart-item--original-price"><s>$${(compareAtPrice / 100).toFixed(2)}</s></p>`
              : ''
          }
        </div>
        <div class="quantity-selector--wrapper">
          <div class="quantity-selector">
            <button data-quantity="-1">-</button>
            <span>${item.quantity}</span>
            <button data-quantity="+1">+</button>
          </div>
          <p class="remove-button">Remove</p>
        </div>
      `;
    }
  
    return div;
  }

  isNewItem(item) {
    const existingItem = this.lastCartItems.find(
      (li) =>
        li.id === item.id &&
        (!!li.selling_plan_allocation === !!item.selling_plan_allocation) &&
        (li.selling_plan_allocation?.selling_plan?.id === item.selling_plan_allocation?.selling_plan?.id)
    );
    return !existingItem || item.quantity > (existingItem.quantity || 0);
  }

  animateNewItem(itemHTML) {
    itemHTML.classList.add('new-item');
    void itemHTML.offsetWidth; // Force reflow
    requestAnimationFrame(() => {
      itemHTML.classList.add('added');
    });
    itemHTML.addEventListener('transitionend', () => {
      itemHTML.classList.remove('new-item', 'added');
    });
  }

  toggleLoading(show) {
    this.drawer.classList.toggle('loading', show);
  }

  openCart() {
    this.drawer.classList.add('cart-drawer--active');
    this.overlay.classList.add('cart-overlay--visible');
    document.body.style.overflow = 'hidden';
  }

  toggleCart() {
    if (this.drawer.classList.contains('cart-drawer--active')) {
      this.closeCart();
    } else {
      this.openCart();
    }
  }

  closeCart() {
    this.drawer.classList.remove('cart-drawer--active');
    this.overlay.classList.remove('cart-overlay--visible');
    document.body.style.overflow = 'auto';
  }
}

customElements.define('cart-drawer-component', CartDrawer);